import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  TablePagination
} from '@mui/material';
import { Activity, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { styled } from '@mui/material/styles';

export default function SpokiWebhooksPage() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1 });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    event: '',
    processing_status: '',
    date_from: '',
    date_to: ''
  });
  const [selectedItem, setSelectedItem] = useState(null);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      const params = { page, limit: rowsPerPage };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const res = await api.get('/spoki/webhooks', { params });
      const data = res.data || {};
      setItems(data.data || []);
      setMeta(data.meta || { page: 1, totalPages: 1 });
    } catch (error) {
      toast.error('Errore nel caricamento webhook Spoki');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      loadData(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filters, rowsPerPage]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePage = (event, newPage) => {
    loadData(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    loadData(1);
  };

  const handleOpenDetails = (item) => {
    setSelectedItem(item);
  };

  const handleCloseDetails = () => {
    setSelectedItem(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Activity size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Webhook Spoki
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Storico eventi e payload in ingresso da Spoki
            </Typography>
          </Box>
        </Box>
      </Box>
      <StyledCard>
        <CardContent>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={filters.event}
              onChange={(e) => handleFilterChange('event', e.target.value)}
              placeholder="Cerca per evento..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 300 }}
            />
            <TextField
              label="Stato elaborazione"
              value={filters.processing_status}
              onChange={(e) => handleFilterChange('processing_status', e.target.value)}
              size="small"
              placeholder="pending, processed, error"
              sx={{ minWidth: 220 }}
            />
            <TextField
              label="Da data"
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="A data"
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>Data ricezione</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell>UUID</TableCell>
                <TableCell>Stato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  onClick={() => handleOpenDetails(item)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    {item.received_at
                      ? new Date(item.received_at).toLocaleString('it-IT')
                      : '-'}
                  </TableCell>
                  <TableCell>{item.event}</TableCell>
                  <TableCell>{item.event_uuid || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={
                        item.processing_status === 'processed'
                          ? 'success'
                          : item.processing_status === 'error'
                          ? 'error'
                          : 'default'
                      }
                      label={item.processing_status}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      Nessun webhook registrato
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={meta.totalPages * rowsPerPage}
            rowsPerPage={rowsPerPage}
            page={meta.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Righe per pagina:"
          />
        </CardContent>
      </StyledCard>
      <Dialog open={!!selectedItem} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Dettaglio webhook</DialogTitle>
        <DialogContent dividers>
          {selectedItem && (
            <Box
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 1,
                p: 1.5,
                fontFamily: 'monospace',
                fontSize: 12,
                maxHeight: 400,
                overflow: 'auto'
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(selectedItem.payload || {}, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));
